import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "../../lib/api";
import { Field, Modal, FormActions, ImageUploader } from "./shared";

const Input = ({ f, value, set }) => {
  const tid = `form-${f.key}`;
  if (f.type === "image") return <ImageUploader label={f.label} value={value || ""} onChange={(v) => set(f.key, v)} />;
  if (f.type === "textarea") return <Field label={f.label} full><textarea data-testid={tid} rows={2} value={value || ""} onChange={(e) => set(f.key, e.target.value)} className="inp" /></Field>;
  if (f.type === "select") return (
    <Field label={f.label}>
      <select data-testid={tid} value={value} onChange={(e) => set(f.key, e.target.value)} className="inp">
        {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
  if (f.type === "checkbox") return (
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 self-end pb-2">
      <input type="checkbox" data-testid={tid} checked={!!value} onChange={(e) => set(f.key, e.target.checked)} /> {f.label}
    </label>
  );
  if (f.type === "color") return (
    <Field label={f.label}>
      <div className="flex gap-2">
        <input type="color" data-testid={`${tid}-picker`} value={value || "#ffffff"} onChange={(e) => set(f.key, e.target.value.toUpperCase())} className="w-11 h-11 rounded-lg border border-gray-300 cursor-pointer bg-white" />
        <input data-testid={tid} required value={value || ""} onChange={(e) => set(f.key, e.target.value)} className="inp font-mono-code uppercase" placeholder="#RRGGBB" pattern="^#[0-9A-Fa-f]{6}$" />
      </div>
    </Field>
  );
  return (
    <Field label={f.label} full={f.full}>
      <input data-testid={tid} type={f.type || "text"} required={f.required} value={value ?? ""} onChange={(e) => set(f.key, e.target.value)} className="inp" placeholder={f.placeholder} />
    </Field>
  );
};

export const SimpleManager = ({ config }) => {
  const { name, title, subtitle, empty, fields, columns, numeric = [] } = config;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await api.adminList(name)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setEditing((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, created_at, ...payload } = editing;
    numeric.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]); });
    try {
      if (id) await api.adminUpdate(name, id, payload);
      else await api.adminCreate(name, payload);
      toast.success(id ? "Actualizado" : "Creado");
      setEditing(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.title || it.name}"?`)) return;
    try { await api.adminDelete(name, it.id); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-500 text-sm">{items.length} {subtitle}</p>
        </div>
        <button data-testid={`add-${name}-btn`} onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E02424] text-white font-bold text-sm hover:bg-[#c01f1f] transition-colors">
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`admin-${name}-table`}>
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  {columns.map((c) => <th key={c.key} className={`px-4 py-3 ${c.right ? "text-right" : "text-left"} ${c.hide ? "hidden md:table-cell" : ""}`}>{c.label}</th>)}
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 && (
                  <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-gray-400">Sin registros todavía.</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} data-testid={`admin-${name}-row-${it.id}`} className="hover:bg-gray-50">
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 ${c.right ? "text-right" : ""} ${c.hide ? "hidden md:table-cell" : ""}`}>
                        {c.render ? c.render(it) : it[c.key]}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-${name}-${it.id}`} onClick={() => setEditing(it)} className="p-2 rounded-lg hover:bg-blue-50 text-[#0067B1]"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`delete-${name}-${it.id}`} onClick={() => remove(it)} className="p-2 rounded-lg hover:bg-red-50 text-[#E02424]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? `Editar ${config.singular}` : `Nuevo ${config.singular}`} onClose={() => setEditing(null)} testId={`${name}-form`}>
          <form onSubmit={save} className="p-6 grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.full || f.type === "image" || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Input f={f} value={editing[f.key]} set={set} />
              </div>
            ))}
            <FormActions onClose={() => setEditing(null)} saving={saving} />
          </form>
        </Modal>
      )}
    </div>
  );
};

export const Thumb = ({ src }) => (
  src ? <img src={assetUrl(src)} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100" /> : <div className="w-12 h-12 rounded-lg bg-gray-100" />
);
